<template>
  <figure>
    <slider-image :slide="slide" />
    <figcaption class="slider-content-wrap">
      <p class="slider-best-rate">{{ $t('best_rate') }}</p>
      <div class="slider-detail-items">
        <div class="slider-title-wrap">
          <div>
            <strong class="best-rate-title" v-html="brrTitle(slide.title)"></strong>
          </div>
          <div>
            <strong class="best-rate-price">{{
              `${showBestPriceWithTax(slide)} ${$tc('for_night', numberOfNights)} (${$t(
                'including_taxes'
              )})`
            }}</strong>
          </div>
        </div>
        <p class="slider-small-title">{{ slide.subtitle }}</p>
        <strong v-if="showCrossOutPrice(slide)" class="cross-out-price">{{
          showCrossOutPrice(slide)
        }}</strong>
      </div>
      <div v-if="slide.detail_items[0]" class="inset">
        <div v-if="showPriceComparision(slide)" class="slider-detail-items no-padding">
          <div
            v-for="(brrDetail, index) in brrDetailItems(slide)"
            :key="`brr-${index}`"
            class="best-rate-comparison"
          >
            <span v-if="brrDetail" class="best-rate-ctext">{{ brrDetail.ota_name }}</span>
            <span v-if="brrDetail && brrDetail.ota_price" class="best-rate-cprice">{{
              brrDetail.ota_price
            }}</span>
            <span v-if="brrDetail && !brrDetail.ota_price" class="best-rate-spinner">
              <spinner-chat-dot />
            </span>
          </div>
        </div>
        <template v-for="(detail, index) in slide.detail_items">
          <div
            :key="index"
            class="slider-detail-items center"
            @click="
              index === 0
                ? changeSearchCriteria(slide.payload, { openPanel: 'openDateWidget' })
                : changeSearchCriteria(slide.payload, { openPanel: 'openPeopleSelector' })
            "
          >
            {{ detail }}
          </div>
        </template>
      </div>
    </figcaption>
    <div class="select">
      <p
        v-for="(action, index) in slide.actions"
        :key="`action-${index}`"
        class="hovering"
        :class="active ? '' : 'inactive'"
        @click="
          index === 0
            ? changeSearchCriteria(slide.payload)
            : choosePlanAction(slide.payload, action)
        "
      >
        <a v-if="action.url" :href="action.url" target="_blank" class="external">{{
          action.text
        }}</a>
        <template v-if="!action.url">{{ action.text }}</template>
      </p>
    </div>
  </figure>
</template>

<script>
import { Chat } from 'src/apis'
import SpinnerChatDot from 'UtilityComponents/SpinnerChatDot.vue'
import SliderImage from 'UtilityComponents/SliderImage.vue'
import { parse, differenceInCalendarDays } from 'date-fns'
import { useBottomPanelStore } from 'Store/bottom-panel'
import { useDatepickerStore } from 'Store/datepicker'
import { useDetailsPageStore } from 'Store/details-page'
import { useReservationForm } from 'Store/reservation-form'
import { useLazyLoadStore } from 'Store/lazy-load'

const messageLimit = 32

export default {
  components: {
    SpinnerChatDot,
    SliderImage
  },

  setup() {
    return {
      bottomPanelStore: useBottomPanelStore(),
      datepickerStore: useDatepickerStore(),
      detailsPageStore: useDetailsPageStore(),
      lazyDataStore: useLazyLoadStore(),
      reservationFormStore: useReservationForm()
    }
  },

  props: {
    chat: { type: Object, required: true },
    slide: { type: Object, required: true }
  },
  data() {
    return {
      active: this.chat.message_rich.hasOwnProperty('active')
        ? this.chat.message_rich.active
        : true,
      scrappingResults: {}
    }
  },
  computed: {
    lazyData() {
      return this.lazyDataStore.lazyData.filter((data) => data.id === this.chat.id)
    },
    numberOfNights() {
      if (
        this.slide.payload.checkin_date === this.slide.payload.checkout_date ||
        !this.slide.payload.checkin_date
      ) {
        return 0
      }
      const checkinDate = parse(this.slide.payload.checkin_date, 'yyyy/MM/dd', new Date())
      const checkoutDate = parse(this.slide.payload.checkout_date, 'yyyy/MM/dd', new Date())
      return differenceInCalendarDays(checkoutDate, checkinDate)
    }
  },
  methods: {
    brrTitle(message) {
      if (message.length > messageLimit) {
        return `${message.substring(0, messageLimit)}...`
      } else {
        return message
      }
    },
    changeSearchCriteria(payload, options = {}) {
      if (!this.active) return

      this.reservationFormStore.setInitialReservationForm({
        startDate: payload.checkin_date,
        endDate: payload.checkout_date,
        adults: payload.adults_number,
        children: payload.kids_number,
        chatID: this.chat.id,
        smoking: payload.smoking,
        payload
      })
      const maxKidAge = this.chat.message_rich.max_kid_age
      if (maxKidAge) this.bottomPanelStore.setMaxKidAge(maxKidAge)
      this.detailsPageStore.setDetailsPageShown('ReservationForm')

      // Reset chat on change criteria
      this.detailsPageStore.setPageChat()

      if (options.openPanel === 'openDateWidget') {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const disabledDates = {
          to: today
        }
        this.datepickerStore.enableDateRange(true)
        this.bottomPanelStore.setBottomPanelInputFor('ReservationForm')
        this.datepickerStore.setDisabledDates(disabledDates)
        this.bottomPanelStore.setBottomPanelComponent('DatePickerPanel')
        this.datepickerStore.setTimeSettings({ currentDate: payload.checkin_date })
      }

      if (options.openPanel === 'openPeopleSelector') {
        this.bottomPanelStore.setBottomPanelInputFor('ReservationForm')
        this.bottomPanelStore.setBottomPanelComponent('PeopleSelector')
      }

      // will save this commented piece until the search button works
      // Chat.bestRoomAction(this.baseURL, 'change_criteria', this.chat.id, payload)
      //   .then(() => { this.active = false })
      //   .catch(err => { console.error('failed to change criteria', err) })
    },
    choosePlanAction(payload, action) {
      if (this.active) {
        let endPoint = 'choose_plan'
        if (action.room_type) {
          endPoint = 'change_criteria'
          payload.room_type = action.room_type
        }
        Chat.bestRoomAction(endPoint, this.chat.id, payload)
          .then(() => {
            this.active = false
          })
          .catch((err) => {
            console.error('failed to choose plan', err)
          })
      }
    },
    sortByOTA(object) {
      // Cloning the object to avoid
      // [Vue warn]: You may have an infinite update loop in a component render function.
      const plans = Object.assign([], object)
      // Sort by ota_name assending
      return plans.sort((a, b) => (a.ota_name > b.ota_name ? 1 : -1))
    },
    roomPlanLazyData(slide) {
      // Returns an array of OTA results
      // [
      //   { ota_name: 'Agoda', ... }
      //   { ota_name: 'Expedia', ... },
      // ]
      const code = slide.payload.selected_room_plan_code
      if (!this.scrappingResults[code]) this.scrappingResults[code] = {}

      const latestState = this.lazyData.filter((data) => data[code]).slice(-1)[0]
      // Merge(update) previous data with latest data
      if (latestState) {
        Object.assign(this.scrappingResults[code], latestState[code])
      }

      return Object.values(this.scrappingResults[code])
    },
    brrDetailItems(slide) {
      if (slide.brr_detail_items[0]) return this.sortByOTA(slide.brr_detail_items)
      if (this.roomPlanLazyData(slide)) return this.sortByOTA(this.roomPlanLazyData(slide))
    },
    showPriceComparision(slide) {
      return this.brrDetailItems(slide) && this.brrDetailItems(slide).length > 0
    },
    bestPriceOTA(slide) {
      if (this.roomPlanLazyData(slide)) {
        return (
          this.roomPlanLazyData(slide)
            // Filter out data without selected_total_rate
            .filter((data) => data.selected_total_rate)
            // Sort by descending order of selected_total_rate
            .sort(function (a, b) {
              return b.selected_total_rate - a.selected_total_rate
            })
            // return the cheapest
            .pop()
        )
      }
    },
    newBestPrice(slide) {
      const bestPrice = this.bestPriceOTA(slide)
      if (bestPrice) return bestPrice.selected_total_rate
    },
    showCrossOutPrice(slide) {
      if (this.newBestPrice(slide)) {
        if (slide.payload.origin_price > this.newBestPrice(slide))
          return slide.payload.origin_price_with_unit
      } else {
        if (slide.payload.origin_price > slide.payload.selected_total_rate)
          return slide.payload.origin_price_with_unit
      }
    },
    showBestPriceWithTax(slide) {
      if (this.newBestPrice(slide)) {
        const bestPrice = this.bestPriceOTA(slide)
        this.updatePayload(slide, bestPrice)
        return bestPrice.new_room_price_with_tax_with_unit
      } else {
        return slide.price_with_tax
      }
    },
    updatePayload(slide, bestPrice) {
      slide.payload.discount_price = bestPrice.discount_price
      slide.payload.selected_total_rate = bestPrice.selected_total_rate
      slide.payload.ota_price = bestPrice.lowest_ota_price
    }
  }
}
</script>
